import { useRef, useState } from 'react';
import { FormApi, FormValues } from 'informed';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { useApiData } from 'lib/api';
import { ApiDownloadButton, Button, CopyButton } from 'components/common';
import { Box, BoxBackLink } from 'components/common/box';
import {
  Checkbox,
  Code,
  Form,
  FormGroup,
  FormSection,
  ImageUploader,
  Select,
  Text
} from 'components/common/form';
import { CertificatesFiles, CertificatesImport, CertificatesPreview } from '.';

import type { CertificateTemplate } from 'shared-types';

const CertificatesEdit = () => {
  const logoUploader = useRef<any>(undefined);
  const formApi = useRef<FormApi<FormValues>>(undefined);

  const { dispatch } = useDispatch();
  const {
    language,
    messages,
    pageState: { templateId },
    theme
  } = useReduxState();

  const [saving, setSaving] = useState(false);

  const { data, loading, updateData } = useApiData({
    onlyIf: Boolean(templateId),
    url: `/admin/certificate-templates/${templateId}`
  });

  const template = (data?.template || null) as CertificateTemplate | null;

  const handleImport = async () => {
    await updateData({ showLoading: true });
  };

  const handleSubmit = async (values) => {
    try {
      setSaving(true);

      if (logoUploader.current.state.imageAdded) {
        const file = await logoUploader.current.upload();
        if (file) values.logo = file.url;
      }
      if (!values.logo) values.logo = null;

      await api.put(`/admin/certificate-templates/${templateId}`, values);

      setSaving(false);
      dispatch.addNotice({
        text: messages.siteCertificatesUpdateSuccess,
        type: 'success'
      });
    } catch (err) {
      setSaving(false);
      await dispatch.handleErrorDispatch(err);
    }
  };

  return (
    <div className="settings">
      <Box
        headerContent={
          <CertificatesImport
            onImport={handleImport}
            templateId={template?.id}
          />
        }
        loading={loading}
        padding={0}
        title={
          <BoxBackLink
            as="/admin/settings/certificates"
            href="/admin/settings/certificates"
            text={messages.siteCertificatesAll}
          />
        }
      >
        {!loading && template ? (
          <Form
            getApi={(informedApi) => (formApi.current = informedApi)}
            initialValues={{
              calendarEventContent: template.calendarEventContent,
              calendarEventFormat: template.calendarEventFormat,
              calendarEventIsSupported: template.calendarEventIsSupported,
              calendarEventLandscape: template.calendarEventLandscape,
              courseContent: template.courseContent,
              courseIsSupported: template.courseIsSupported,
              courseFormat: template.courseFormat,
              courseLandscape: template.courseLandscape,
              data: template.data,
              dateFormat: template.dateFormat,
              logo: template.logo,
              pathContent: template.pathContent,
              pathIsSupported: template.pathIsSupported,
              pathFormat: template.pathFormat,
              pathLandscape: template.pathLandscape,
              title: template.title
            }}
            onSubmit={handleSubmit}
          >
            {({ formState }) => (
              <div>
                <FormSection maxWidth="100%">
                  <FormGroup label={messages.commonTitle}>
                    <Text
                      field="title"
                      style={{ maxWidth: '100%', width: 300 }}
                    />
                  </FormGroup>

                  <FormGroup label={'Supported templates'}>
                    <Checkbox
                      field="courseIsSupported"
                      id="certificate-template-course-is-supported"
                      label={messages.commonCourses}
                    />
                    <Checkbox
                      field="pathIsSupported"
                      id="certificate-template-path-is-supported"
                      label={messages.commonPaths}
                      style={{ marginTop: 5 }}
                    />
                    <Checkbox
                      field="calendarEventIsSupported"
                      id="certificate-template-calendar-event-is-supported"
                      label={'Calendar events'}
                      style={{ marginTop: 5 }}
                    />
                  </FormGroup>

                  <FormGroup label={messages.commonLogo}>
                    <ImageUploader
                      field="logo"
                      id="certificate-template-logo"
                      maxWidth="120px"
                      messages={messages}
                      setUploader={(imageUploader) =>
                        (logoUploader.current = imageUploader)
                      }
                    />
                  </FormGroup>

                  <FormGroup label={messages.siteCertificatesDateFormat}>
                    <Text
                      field="dateFormat"
                      style={{ maxWidth: '100%', width: 300 }}
                    />
                  </FormGroup>

                  <FormGroup
                    label={messages.commonFiles}
                    style={{ maxWidth: '100%' }}
                  >
                    <CertificatesFiles
                      onUpdate={updateData}
                      template={template}
                    />
                  </FormGroup>

                  <FormGroup label={messages.commonData} maxWidth="100%">
                    <Code field="data" style={{ fontSize: '1.3rem' }} />
                  </FormGroup>

                  <div className="course">
                    <FormGroup
                      label={messages.siteCertificatesCourseContent}
                      maxWidth="100%"
                    >
                      <Code
                        field="courseContent"
                        style={{ fontSize: '1.3rem' }}
                      />
                    </FormGroup>

                    <div className="row-md">
                      <div className="col">
                        <FormGroup
                          label={messages.commonLayout}
                          style={{ marginTop: 10 }}
                        >
                          <Select
                            field="courseFormat"
                            id="certificate-template-course-format"
                            options={[
                              {
                                label: 'A4',
                                value: 'A4'
                              },
                              {
                                label: 'A5',
                                value: 'A5'
                              }
                            ]}
                            style={{ maxWidth: '100%', width: 300 }}
                          />
                        </FormGroup>

                        <FormGroup>
                          <Checkbox
                            field="courseLandscape"
                            id="certificate-template-course-landscape"
                            label={messages.siteCertificatesLandscape}
                          />
                        </FormGroup>
                      </div>
                      <div className="col-min">
                        <div className="preview">
                          <CertificatesPreview
                            certificateType="course"
                            content={formState.values.courseContent}
                            data={formState.values.data}
                            dateFormat={formState.values.dateFormat}
                            format={formState.values.courseFormat}
                            landscape={formState.values.courseLandscape}
                            templateId={templateId}
                          />
                          <ApiDownloadButton
                            filename={'course-certificate.pdf'}
                            icon="file-pdf"
                            style={{ marginLeft: 10 }}
                            text={messages.commonDownloadPdf}
                            url={
                              `/admin/certificate-templates/${template.id}/preview` +
                              `?language=${language}&type=course`
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="path">
                    <FormGroup
                      label={messages.siteCertificatesPathContent}
                      maxWidth="100%"
                    >
                      <Code
                        field="pathContent"
                        style={{ fontSize: '1.3rem' }}
                      />
                    </FormGroup>

                    <div className="row-md">
                      <div className="col">
                        <FormGroup
                          label={messages.commonLayout}
                          style={{ marginTop: 10 }}
                        >
                          <Select
                            field="pathFormat"
                            id="certificate-template-path-format"
                            options={[
                              {
                                label: 'A4',
                                value: 'A4'
                              },
                              {
                                label: 'A5',
                                value: 'A5'
                              }
                            ]}
                            style={{ maxWidth: '100%', width: 300 }}
                          />
                        </FormGroup>

                        <FormGroup>
                          <Checkbox
                            field="pathLandscape"
                            id="certificate-template-path-landscape"
                            label={messages.siteCertificatesLandscape}
                          />
                        </FormGroup>
                      </div>
                      <div className="col-min">
                        <div className="preview">
                          <CertificatesPreview
                            certificateType="path"
                            content={formState.values.pathContent}
                            data={formState.values.data}
                            dateFormat={formState.values.dateFormat}
                            format={formState.values.pathFormat}
                            landscape={formState.values.pathLandscape}
                            templateId={templateId}
                          />
                          <ApiDownloadButton
                            filename={'path-certificate.pdf'}
                            icon="file-pdf"
                            style={{ marginLeft: 10 }}
                            text={messages.commonDownloadPdf}
                            url={
                              `/admin/certificate-templates/${template.id}/preview` +
                              `?language=${language}&type=path`
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="calendar-event">
                    <FormGroup
                      label={messages.siteCertificatesCalendarEventContent}
                      maxWidth="100%"
                    >
                      <Code
                        field="calendarEventContent"
                        style={{ fontSize: '1.3rem' }}
                      />
                    </FormGroup>

                    <div className="row-md">
                      <div className="col">
                        <FormGroup
                          label={messages.commonLayout}
                          style={{ marginTop: 10 }}
                        >
                          <Select
                            field="calendarEventFormat"
                            id="certificate-template-calendar-event-format"
                            options={[
                              {
                                label: 'A4',
                                value: 'A4'
                              },
                              {
                                label: 'A5',
                                value: 'A5'
                              }
                            ]}
                            style={{ maxWidth: '100%', width: 300 }}
                          />
                        </FormGroup>

                        <FormGroup>
                          <Checkbox
                            field="calendarEventLandscape"
                            id="certificate-template-calendar-event-landscape"
                            label={messages.siteCertificatesLandscape}
                          />
                        </FormGroup>
                      </div>
                      <div className="col-min">
                        <div className="preview">
                          <CertificatesPreview
                            certificateType="calendar-event"
                            content={formState.values.calendarEventContent}
                            data={formState.values.data}
                            dateFormat={formState.values.dateFormat}
                            format={formState.values.calendarEventFormat}
                            landscape={formState.values.calendarEventLandscape}
                            templateId={templateId}
                          />
                          <ApiDownloadButton
                            filename={'calendar-event-certificate.pdf'}
                            icon="file-pdf"
                            style={{ marginLeft: 10 }}
                            text={messages.commonDownloadPdf}
                            url={
                              `/admin/certificate-templates/${template.id}/preview` +
                              `?language=${language}&type=calendar-event`
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </FormSection>

                <FormSection maxWidth="100%">
                  <div className="footer">
                    <Button
                      category="success"
                      disabled={formState.invalid}
                      loading={saving}
                      text={messages.commonSaveChanges}
                      type="submit"
                    />

                    <CopyButton
                      aria-label={messages.commonExportData}
                      icon="copy"
                      text={messages.commonExportData}
                      successText={messages.commonCopied}
                      value={JSON.stringify({
                        ...formState.values,
                        files: template?.files || []
                      })}
                    />
                  </div>
                </FormSection>
              </div>
            )}
          </Form>
        ) : null}
      </Box>

      <style jsx>{`
        .content {
          margin-top: 20px;
        }
        .render-error {
          color: ${theme.dangerColor};
          font-size: 1.3rem;
          padding: 3px;
        }
        .course,
        .path,
        .calendar-event {
          margin-top: 50px;
        }
        .preview {
          margin-top: 15px;
        }
        .footer {
          display: flex;
          gap: 15px;
          justify-content: space-between;
        }
      `}</style>
    </div>
  );
};

export default CertificatesEdit;
